import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
  ElementRef,
  Renderer2,
  OnDestroy,
} from '@angular/core';
import { Option } from '../core-dropdown/core-dropdown.component';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-core-multiselect',
  templateUrl: './core-multiselect.component.html',
  styleUrls: ['./core-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoreMultiSelectComponent),
      multi: true,
    },
  ],
})
export class CoreMultiSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options: any[];
  @Input() type: 'group' | 'single' | 'string';
  @Input() value: any[] = [];
  @Input() placeholder:string='';
  @Output() valueChange = new EventEmitter<any[]>();
  @Output() changeOn = new EventEmitter<any>();
  @Input() response: 'both' | 'value' = 'value';
  @Input() version: 'form' | 'ngModel' = 'ngModel';
  @Input() disabled: boolean = false;
  @Input() fetchGroupInfo:boolean=false; // returns value with group info
  @Input() formGroupParent: FormGroup;
  @Input() formControlName: string;
  @Input() filter: boolean = true;

  isOpen = false;
  searchText = '';
  filteredOptions: any[];
  selectedLabel: string;

  private clickListener: (() => void) | null = null;

  constructor(private readonly  translate: TranslateService,
    private readonly eRef: ElementRef, private readonly renderer: Renderer2
  ){}

  ngOnInit() {
    this.filteredOptions = this.options;
    if (this.version === 'form') {
      if (this.formGroupParent && this.formControlName) {
        this.formGroupParent.get(
          this.formControlName
        ) as FormControl;
      }
    } else {
      this.updateSelectedLabel();
    }
  }

  /* Form functions */

  private propagateChange = (_: any) => {};
  private propagateTouched = () => {};

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedLabel();
    this.propagateChange(value);
  }

  /* Form functions end */

  /* common functions */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange) {
      this.filteredOptions = this.options;
      this.updateSelectedLabel();
    }

    if (
      this.version === 'ngModel' &&
      changes['value'] &&
      !changes['value']?.firstChange
    ) {
      this.updateSelectedLabel();
    }
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickListener();
    } else {
      this.removeClickListener();
    }
  }

  reset() {
    this.value = [];
    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
    this.selectedLabel = '';
  }

  updateSelectedLabel() {
    switch (this.type) {
      case 'single':
        this.setSingleSelectLabel();
        break;
      case 'group':
        this.setGroupSelectLabel();
        break;
      case 'string':
        this.setStringSelectLabel();
        break;
    }
  }

  /* common functions end */

  /* group functions */

  filterGroupOptions() {
    this.filteredOptions = this.options
      .map((group) => ({
        ...group,
        items: group.items.filter((item: any) =>
          item.label.toLowerCase().includes(this.searchText.toLowerCase())
        ),
      }))
      .filter((group) => group.items.length > 0);
  }

  toggleGroupOption(option: any, group: any) {
    const index = this.value.findIndex(
      (v: Option) => v.value === option.value
    );

    if (index > -1) {
      // Mevcutsa listeden çıkar
      this.value.splice(index, 1);
    } else {
      // Yeni öğe ekle
      const response = this.buildResponse(option, group);
      this.value.push(response);
    }

    // Form veya event update
    this.updateValue();
    this.updateSelectedLabel();
  }

  private buildResponse(option: any, group: any) {
    if (this.response === 'value') {
      return this.fetchGroupInfo
        ? { group: group?.value, value: option?.value }
        : option?.value;
    } else if (this.response === 'both') {
      return this.fetchGroupInfo
        ? { group: { label: group?.label, value: group?.value }, label: option?.label, value: option?.value }
        : { label: option?.label, value: option?.value };
    }
    return null;
  }

  private updateValue() {
    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
  }

  isAllGroupSelected() {
    let allSelected = true;
    for(const group of this.filteredOptions){
      for(const item of group.items){
        if (!this.isSelected(item.value)) {
          allSelected = false;
        }
      }
    }
    return allSelected;
  }

  toggleSelectAllGroup(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectAllGroups();
    } else {
      this.value = [];
    }

    this.emitChanges();
  }

  private selectAllGroups(): void {
    for (const group of this.filteredOptions) {
      for (const item of group.items) {
        if (!this.isSelected(item.value)) {
          this.addItemToValue(group, item);
        }
      }
    }
  }

  private addItemToValue(group: any, item: any): void {
    if (this.response === 'value') {
      if (this.fetchGroupInfo) {
        this.value.push({ group: group?.value, value: item?.value });
      } else {
        this.value.push(item?.value);
      }
    } else if (this.response === 'both') {
      if (this.fetchGroupInfo) {
        this.value.push({
          group: { label: group?.label, value: group?.value },
          label: item?.label,
          value: item?.value,
        });
      } else {
        this.value.push({
          label: item?.label,
          value: item?.value,
        });
      }
    }
  }

  private emitChanges(): void {
    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
    this.updateSelectedLabel();
  }


  setGroupSelectLabel() {
    if (this.value.length === 0) {
      this.selectedLabel = '';
    } else if (this.value.length === 1) {
      const val = this.value[0]?.value ?? this.value[0];
      this.selectedLabel = ''; // Önce varsayılan boş

      for (const group of this.options) {
        const found = group.items.find((item: Option) => item.value === val);
        if (found) {
          this.selectedLabel = found.label;
          return;
        }
      }
    } else {
      this.selectedLabel = `${this.value.length} ${this.translate.instant('dropdown.option.selected')}`;
    }
  }
  /* group functionns end */

  /* single functions */

  filterOptions() {
    this.filteredOptions = this.options.filter((option) =>
      option.label.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  isSelected(value: any) {
    const values = this.value?.find((selVal) => {
      const val = selVal.value ?? selVal;
      if (val === value) {
        return true;
      }
      return false;
    });
    return values;
  }

  toggleOption(option: Option) {
    if (this.response === 'value') {
      const index = this.value.indexOf(
        (values: Option) => values === option.value
      );
      if (index > -1) {
        this.value.splice(index, 1);
      } else {
        this.value.push(option.value);
      }
    } else if (this.response === 'both') {
      const index = this.value.findIndex(
        (values: Option) => values.value === option.value
      );
      if (index > -1) {
        this.value.splice(index, 1);
      } else {
        this.value.push(option);
      }
    }

    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
    this.updateSelectedLabel();
  }

  isAllSelected() {
    for (const valItem of this.value) {
      const value = valItem?.value ?? valItem;
      const isInclude = this.filteredOptions.find(
        (item: Option) => item.value === value
      );
      if (!isInclude) {
        return false;
      }
    }
    return this.filteredOptions.length === this.value.length;
  }

  toggleSelectAll(event: Event) {
    if ((event.target as HTMLInputElement).checked) {
      if (this.response === 'both') {
        this.value = this.filteredOptions.map((option) => option);
      } else {
        this.value = this.filteredOptions.map((option) => option.value);
      }
    } else {
      this.value = [];
    }
    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
    this.updateSelectedLabel();
  }

  setSingleSelectLabel() {
    if (this.value.length === 0) {
      this.selectedLabel = '';
    } else if (this.value.length === 1) {
      const val = this.value[0]?.value ?? this.value[0];
      this.selectedLabel = this.options.find(
        (option) => option.value === val
      )?.label;
    } else {
      this.selectedLabel = `${this.value.length} ${this.translate.instant('dropdown.option.selected')}`;
    }
  }
  /* single functions end */

  /* string functions  */
  filterStringOptions() {
    this.filteredOptions = this.options.filter((option) =>
      option?.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  toggleStringOption(option: string) {
    const index = this.value.indexOf((opt:any) => opt === option);
    if (index > -1) {
      this.value.splice(index, 1);
    } else {
      this.value.push(option);
    }
    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
    this.updateSelectedLabel();
  }

  isAllStringSelected() {
    for (const value of this.value) {
      const isInclude = this.filteredOptions.find((item: Option) => item === value);
      if (!isInclude) {
        return false;
      }
    }
    return this.filteredOptions.length === this.value.length;
  }


  toggleSelectAllString(event: Event) {
    if ((event.target as HTMLInputElement).checked) {
      this.value = [...this.filteredOptions];
    } else {
      this.value = [];
    }
    if (this.version === 'form') {
      this.formGroupParent.patchValue({ [this.formControlName]: this.value });
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit(this.value);
    this.updateSelectedLabel();
  }

  setStringSelectLabel() {
    if (this.value.length === 0) {
      this.selectedLabel = '';
    } else if (this.value.length === 1) {
      this.selectedLabel = this.options.find(
        (option) => option === this.value[0]
      );
    } else {
      this.selectedLabel = `${this.value.length} ${this.translate.instant('dropdown.option.selected')}`;
    }
  }
  /* string functions end */



  private addClickListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (!this.eRef.nativeElement.contains(event.target)) {
        this.isOpen = false;
        this.removeClickListener();
      }
    });
  }

  private removeClickListener() {
    if (this.clickListener) {
      this.clickListener = null;
    }
  }

  ngOnDestroy() {
    this.removeClickListener();
  }
}
